import { memo, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { flushSync } from 'react-dom';
import { motion, useMotionValue, animate } from 'framer-motion';

import TopNav from '@/components/layout/TopNav';
import BottomSheet, { SHEET_HEIGHT } from '@/components/layout/BottomSheet';
import PlayerCard, {
  type PlayerCardProps,
  type PlayerCardState,
} from '@/components/cards/PlayerCard';
import { useFitScale } from '@/utils/useFitScale';
import RevealAnimationOverlay, {
  revealAnimationUrl,
} from '@/components/cards/RevealAnimationOverlay';
import RevealScreen from '@/views/RevealScreen';
import {
  pickTodaysRound,
  useEnterRound,
  useLobby,
  useRevealSlot,
  useSelectCandidate,
} from '@/api/useLobby';
import { CyclingPrize } from '@/components/common/CyclingPrize';
import { usePrefetchLeaderboards, type LeaderboardPeriod } from '@/api/useLeaderboard';
import { useActiveMatches, type ActiveMatch } from '@/api/useActiveMatches';
import type { LobbyPlayer, LobbyRound, LobbySlot } from '@/api/lobby.types';
import { useNow } from '@/utils/useNow';
import { formatCardCountdown } from '@/utils/formatCardCountdown';
import { clubName } from '@/utils/clubs';
import i18n from '@/i18n';
import { useTranslation } from 'react-i18next';

const SLIDE_EASE = [0.32, 0.72, 0, 1] as const;
const SLIDE_DURATION = 0.5;

interface RoundOpensInProps {
  round: LobbyRound;
}

/** Self-clocking "opens in X Days / Xh Xm" for empty/upcoming rounds. */
function RoundOpensIn({ round }: RoundOpensInProps) {
  const { t } = useTranslation();
  const now = useNow(1000);
  if (!round.revealDays[0]) return null;
  return (
    <>
      {t('lobby.opensIn', {
        round: round.name,
        time: formatCardCountdown(round.revealDays[0].revealStart, now),
      })}
    </>
  );
}

/**
 * Slot status → card face. `available` is the slot that can be opened right now,
 * so it maps to the `reveal` face; `pending` stays `upcoming` and the API's
 * `missed` surfaces as `expired`.
 */
function cardStateFor(slot: LobbySlot | null): PlayerCardState {
  if (!slot) return 'upcoming';
  if (slot.status === 'missed') return 'expired';
  if (slot.status === 'available') return 'reveal';
  if (slot.status === 'revealed') {
    if (!slot.player) return 'choose';
    return slot.revealedRarity === 'rare' ? 'revealed-rare' : 'revealed-common';
  }
  return 'upcoming';
}

/** Bottom plate: the fixture before kickoff, the score once it starts. */
function formatPlate(
  match: ActiveMatch | undefined,
  code: string | null | undefined
): string | undefined {
  if (!match?.homeTeamCode || !match.awayTeamCode) return undefined;
  const home = match.homeTeamCode.toUpperCase();
  const away = match.awayTeamCode.toUpperCase();
  if (match.status === 'postponed') return `${home} PP ${away}`;
  if (match.status === 'upcoming') {
    const isHome = code?.toUpperCase() === home;
    const time = match.kickoffAt
      ? new Intl.DateTimeFormat(i18n.language, {
          hour: '2-digit',
          minute: '2-digit',
          hourCycle: 'h23',
          timeZone: 'Europe/Brussels',
        }).format(new Date(match.kickoffAt))
      : '';
    const club = clubName(isHome ? away : home);
    return i18n.t(isHome ? 'lobby.homeFixture' : 'lobby.awayFixture', { club, time }).trim();
  }
  const score = `${home} ${match.homeScore} : ${match.awayScore} ${away}`;
  if (match.status === 'half_time') return `${score} HT`;
  if (match.status === 'live' && match.minute != null) return `${score} ${match.minute}'`;
  return score;
}

/** Day and month of a moment in the contest's zone, e.g. "26 Oct", in the app's language. */
function formatCardDate(iso: string): string {
  return new Intl.DateTimeFormat(i18n.language, {
    day: 'numeric',
    month: 'short',
    timeZone: 'Europe/Brussels',
  }).format(new Date(iso));
}

/** Tally line for a revealed card, in the order of the round totals bar. Blank until kickoff. */
function formatStats(slot: LobbySlot, match: ActiveMatch | undefined): string | undefined {
  if (!match || match.status === 'upcoming' || match.status === 'postponed') return undefined;
  return i18n.t('lobby.stats', {
    tackles: slot.tackleCount,
    interceptions: slot.interceptionCount,
    shots: slot.shotCount,
    goals: slot.goalCount,
  });
}

/** A fixture counts as in play from kickoff to the final whistle, half-time included. */
const isLive = (match: ActiveMatch | undefined) =>
  match?.status === 'live' || match?.status === 'half_time';

interface RoundContentProps {
  round: LobbyRound;
  matches: ActiveMatch[];
  /** Which period's prizes the line above the sheet shows, following its toggle. */
  period: LeaderboardPeriod;
  /** The contest's pack size; the animation for a demo card has no slot to count. */
  cardsPerReveal: number;
  onRevealSlot: (slotId: string) => Promise<unknown>;
  onSelectCandidate: (slotId: string, candidateId: string) => Promise<unknown>;
  /** Why auto-entry failed, shown in place of the drawing notice; a reload retries. */
  entryError?: string;
}

function RoundContentInner({
  round,
  matches,
  period,
  cardsPerReveal,
  onRevealSlot,
  onSelectCandidate,
  entryError,
}: RoundContentProps) {
  const { t } = useTranslation();
  const now = useNow(1000);
  const slots = round.userRound?.slots ?? [];
  // Weekly and monthly tiers are configured separately, so the line shows the
  // rewards for whichever board is on screen rather than always the round's.
  const activePrizes = period === 'week' ? round.prizes : (round.month?.prizes ?? []);
  // Every reveal window has closed — nothing in this round can be opened now.

  // The whole squad's image URL is known at draw, so warm them while the video
  // plays. Decoded and retained rather than just fetched: the bytes alone still
  // cost a decode on first paint, which is what the carousel flashes on.
  const warmed = useRef<HTMLImageElement[]>([]);
  useEffect(() => {
    const urls = slots.flatMap(slot =>
      slot.candidates.map(c => c.player.imageUrl).filter((u): u is string => !!u)
    );
    warmed.current = urls.map(url => {
      const img = new Image();
      img.src = url;
      img.decode().catch(() => {});
      return img;
    });
  }, [slots]);

  // The pack animation is 4 MB; fetch it while the lobby is idle so the first open doesn't stall.
  const cardsPerPack = slots[0]?.candidates.length || cardsPerReveal;
  useEffect(() => {
    fetch(revealAnimationUrl(cardsPerPack)).catch(() => {});
  }, [cardsPerPack]);

  const roundEnded =
    round.revealDays.length > 0 &&
    round.revealDays.every(d => new Date(d.revealEnd).getTime() <= now);
  // The entry's slots in day order; the rows always render five cards, so
  // positions past the end show the upcoming state. Pending slots are included
  // so each carries a countdown to its own window rather than a placeholder, and
  // a revealed slot is included whether or not a player has been chosen, since
  // `player` stays null until selection.
  const resolved = slots;

  // The pack animation plays over a black backing; once it starts dissolving the
  // reveal screen mounts beneath it, and the overlay unmounts when it ends.
  // Null slotId means a demo card — it animates, but there's nothing to persist.
  const [revealing, setRevealing] = useState<{
    slotId: string | null;
    phase: 'video' | 'screen';
    animating: boolean;
  } | null>(null);

  const [revealError, setRevealError] = useState<string | null>(null);

  const startReveal = (i: number) => {
    // Ignore taps once a reveal is under way — a double-tap would otherwise
    // restart the video and fire the mutation twice.
    if (revealing) return;
    const slot = resolved[i] ?? null;
    // An already-revealed slot re-enters at the carousel: replaying the
    // video would re-POST the reveal, which the backend rejects with a 409
    const phase = slot?.status === 'revealed' ? 'screen' : 'video';
    setRevealing({ slotId: slot?.id ?? null, phase, animating: phase === 'video' });
    // Fired here rather than from the video's onComplete so the round-trip
    // overlaps playback. Demo cards have no slot to post.
    if (phase === 'video' && slot?.id) {
      onRevealSlot(slot.id).catch(() => setRevealError(t('lobby.openError')));
    }
  };

  // The slot being revealed, re-read from live data each render: the reveal
  // mutation's refetch fills in `player` mid-flow, so the reveal screen shows
  // the real drawn card as soon as the round-trip lands.
  const revealingSlot = revealing?.slotId
    ? (slots.find(s => s.id === revealing.slotId) ?? null)
    : null;
  // One source of truth for what a revealed face shows, used by the squad row
  // and the reveal carousel alike.
  const identity = (player: LobbyPlayer | null | undefined) =>
    player
      ? {
          firstName: player.knownName
            ? undefined
            : (player.shortFirstName ?? player.firstName ?? undefined),
          lastName: player.knownName ?? player.shortLastName ?? player.lastName ?? undefined,
          shirtNumber: player.shirtNumber,
          nationality: player.nationality,
          position: player.position ? t(`position.${player.position}`) : undefined,
          club: clubName(player.teamCode),
          imageUrl: player.imageUrl,
        }
      : {};
  // The player's fixture in this round: the only link between the two payloads
  // is the club code appearing on one side of the match.
  const matchFor = (code: string | null | undefined) =>
    code
      ? matches.find(
          m => m.roundId === round.id && (m.homeTeamCode === code || m.awayTeamCode === code)
        )
      : undefined;
  const cardForPlayer = (
    player: LobbyPlayer,
    rarity: LobbySlot['revealedRarity']
  ): PlayerCardProps => ({
    state: rarity === 'rare' ? 'revealed-rare' : 'revealed-common',
    ...identity(player),
    plate: formatPlate(matchFor(player.teamCode), player.teamCode),
  });

  const cardPropsFor = (i: number): PlayerCardProps => {
    const slot = resolved[i] ?? null;
    const code = slot?.player?.teamCode;
    const match = matchFor(code);
    // A position with no slot in an ended round was never openable and never
    // will be — expired, not "reveal in…". Covers viewing a round that ran
    // its course without an entry.
    const state = !slot && roundEnded ? 'expired' : cardStateFor(slot);
    const isPackFace = state === 'reveal' || state === 'choose';
    /*
      Pack faces count down to their window closing, upcoming slots to theirs
      opening. The round's schedule is the fallback source so a round the user
      has not entered still counts down honestly. An empty result means the
      moment has passed, which blanks the label rather than falling through to
      DEFAULT_LABELS.
    */
    const countsDownTo = isPackFace
      ? (slot?.revealEnd ?? null)
      : state === 'upcoming'
        ? (slot?.revealStart ?? round.revealDays.find(d => d.dayIndex === i)?.revealStart ?? null)
        : null;
    const remaining = countsDownTo ? formatCardCountdown(countsDownTo, now) : null;
    // An expired face says when its window closed; the slot knows, or the round's schedule does.
    const closedAt =
      state === 'expired'
        ? (slot?.revealEnd ?? round.revealDays.find(d => d.dayIndex === i)?.revealEnd ?? null)
        : null;
    const base: PlayerCardProps = {
      state,
      label: closedAt
        ? t('card.expiredOn', { date: formatCardDate(closedAt) })
        : remaining === null
          ? undefined
          : remaining && t(isPackFace ? 'lobby.expiresIn' : 'lobby.revealIn', { time: remaining }),

      ...identity(slot?.player),
      plate: formatPlate(match, code),
      stats: slot?.player ? formatStats(slot, match) : undefined,
      live: !!slot?.player && isLive(match),
    };
    // Both pack faces open the reveal flow; startReveal decides whether the
    // video plays or it re-enters at the carousel.
    return isPackFace ? { ...base, onClick: () => startReveal(i) } : base;
  };

  const fitRef = useRef<HTMLDivElement>(null);
  const cardScale = useFitScale(fitRef);

  return (
    <div
      className="w-full min-h-full flex flex-col"
      style={{ paddingBottom: `calc(${SHEET_HEIGHT} + 16px)` }}
    >
      <div className="shrink-0 flex flex-col gap-1 px-4 pt-2">
        {activePrizes.length > 0 && (
          <div className="flex w-full items-center justify-center gap-[6px]">
            <span className="shrink-0 font-poppins font-medium text-[10px] leading-none text-[#898E9A]">
              {period === 'week' ? t('lobby.prize') : t('lobby.monthlyPrize')}
            </span>
            <CyclingPrize prizes={activePrizes} />
          </div>
        )}
        {/*
          The player's own totals for this round, rolled up from the events
          ledger. A dash means they have not entered — an entered squad that has
          scored nothing shows 0, which is a different thing and reads as one.
        */}
        <div className="w-full h-[30px] rounded-[9px] bg-[#222429] border border-[#2E3441] flex items-center px-[6px]">
          {[
            { label: t('lobby.totals.tackles'), value: round.userRound?.tackleCount },
            { label: t('lobby.totals.intercepts'), value: round.userRound?.interceptionCount },
            { label: t('lobby.totals.shots'), value: round.userRound?.shotCount },
            { label: t('lobby.totals.goals'), value: round.userRound?.goalCount },
            { label: t('lobby.totals.pts'), value: round.userRound?.score },
          ].map(({ label, value }) => (
            <div key={label} className="flex-1 flex flex-col items-center gap-[3px]">
              <span className="font-poppins font-medium text-[9px] leading-none text-center text-[#898E9A]">
                {label}
              </span>
              <span className="font-poppins font-semibold text-[11px] leading-none text-center text-white">
                {value ?? '--'}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/*
        The squad is scaled to fit its box on both axes (see useFitScale), so all
        five cards stay visible without scrolling on short screens and grow to
        fill the space on tall ones. Width binds on narrow phones, height on
        short ones. Every offset inside the card is tuned to the 110×148 box and
        scales with --player-card-scale, including the fan offset below.
      */}

      <div
        ref={fitRef}
        className="relative flex-1 min-h-0 flex flex-col justify-center overflow-hidden"
        style={{ '--player-card-scale': cardScale } as CSSProperties}
      >
        <div className="flex items-start justify-center gap-4 pt-2">
          {[0, 1, 2].map(i => (
            // Outer cards sit lower than the middle one, so the row reads as a fan.
            <div
              key={i}
              className={i === 1 ? undefined : 'pt-[calc(65px*var(--player-card-scale))]'}
            >
              <PlayerCard {...cardPropsFor(i)} />
            </div>
          ))}
        </div>

        <div className="flex justify-center gap-8 pt-2">
          {[3, 4].map(i => (
            <PlayerCard key={i} {...cardPropsFor(i)} />
          ))}
        </div>

        {/* Centred over the whole card cluster; inert so it can't swallow card taps. */}
        <img
          src="/main-logo.png"
          alt=""
          className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[101px] h-[46px] opacity-40"
        />
      </div>

      {/* Ended rounds say it with the expired card faces — no text needed. */}
      {slots.length === 0 && !roundEnded && (
        <div className="px-4 pt-6 text-sm text-[#9AA9B7]">
          {round.revealDays[0] && new Date(round.revealDays[0].revealStart).getTime() > now ? (
            <RoundOpensIn round={round} />
          ) : (
            // Open round, no slots yet: auto-entry is registering the user and
            // pre-drawing the squad; the refetch fills the cards in.
            // TODO: Replace with loading screen?
            (entryError ?? t('lobby.drawing'))
          )}
        </div>
      )}

      {/*
        Pitch backing sits under both phases so the reveal screen has something
        opaque to fade up against — without it the lobby shows through mid-fade,
        which reads as a flash rather than a transition.
      */}
      {revealing && (
        <div
          aria-hidden
          className="fixed inset-0 z-40 bg-[#161616] bg-cover bg-bottom bg-no-repeat"
          style={{ backgroundImage: 'url(/field.png)' }}
        />
      )}

      {revealing?.animating && (
        <RevealAnimationOverlay
          cards={revealingSlot?.candidates.length || cardsPerReveal}
          onHandover={() => setRevealing(r => (r ? { ...r, phase: 'screen' } : r))}
          onComplete={() => setRevealing(r => (r ? { ...r, animating: false } : r))}
        />
      )}

      {/* Clearing `revealing` drops the screen and the black backing together,
          so the lobby is uncovered in one step rather than flashing black. */}
      {revealing?.phase === 'screen' && (
        <RevealScreen
          cards={(revealingSlot?.candidates ?? []).map(c => {
            const isSelected = c.id === revealingSlot?.selectedCandidateId;
            return {
              id: c.id,
              isSelected,
              // The committed pick prefers the slot's snapshot so a later pool
              // edit can't change the card the user kept; unselected candidates
              // have no snapshot and carry their own rarity.
              card: cardForPlayer(
                c.player,
                (isSelected && revealingSlot?.revealedRarity) || c.player.rarity
              ),
            };
          })}
          onSelectCandidate={candidateId => {
            if (!revealingSlot) return;
            setRevealError(null);
            onSelectCandidate(revealingSlot.id, candidateId).catch((e: { statusCode?: number }) => {
              // 409 means the slot was already committed elsewhere; the hook
              // refetches and the locked card arrives on its own.
              if (e?.statusCode === 409) return;
              setRevealError(t('lobby.lockError'));
            });
          }}
          onContinue={() => {
            setRevealError(null);
            setRevealing(null);
          }}
          error={revealError}
        />
      )}
    </div>
  );
}

const RoundContent = memo(RoundContentInner) as typeof RoundContentInner;

export default function LobbyView() {
  const { data, isLoading, isError } = useLobby();
  const { data: matchesData } = useActiveMatches();
  const matches = matchesData?.matches ?? [];
  // Coarse clock for "is this round open?" only — countdowns live in their
  // own self-clocking components so the lobby doesn't re-render every tick.
  const now = useNow(30_000);

  const [selectedRoundId, setSelectedRoundId] = useState<string | null>(null);
  // Owned here, not in the sheet, so the prize line above it follows the toggle.
  const [period, setPeriod] = useState<LeaderboardPeriod>('week');
  const dragX = useMotionValue(0);
  const trackRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const isHorizontalDrag = useRef<boolean | null>(null);

  const openRounds = useMemo(() => {
    const nowIso = new Date().toISOString();
    const sortedAll = (data?.rounds ?? []).slice().sort((a, b) => a.sequence - b.sequence);
    const isOpen = (r: LobbyRound) => r.revealDays.some(d => d.revealStart <= nowIso);
    const lastOpenIdx = sortedAll.reduce((best, r, i) => (isOpen(r) ? i : best), -1);
    const visible = sortedAll.slice(0, lastOpenIdx === -1 ? 2 : lastOpenIdx + 2);
    return visible.filter(isOpen);
  }, [data?.rounds, now]);

  const currentOpenIdx = openRounds.findIndex(r => r.id === selectedRoundId);
  const prevRound = currentOpenIdx > 0 ? openRounds[currentOpenIdx - 1] : null;
  const nextRound = currentOpenIdx < openRounds.length - 1 ? openRounds[currentOpenIdx + 1] : null;
  // Taps on the round strip take the same slide as a swipe when the target is a neighbour.
  const slideTo = (roundId: string) => {
    const direction = roundId === prevRound?.id ? 1 : roundId === nextRound?.id ? -1 : 0;
    if (direction === 0) {
      setSelectedRoundId(roundId);
      return;
    }
    const width = trackRef.current?.clientWidth ?? window.innerWidth;
    animate(dragX, direction * width, {
      duration: 0.28,
      ease: [...SLIDE_EASE],
      onComplete: () => {
        flushSync(() => setSelectedRoundId(roundId));
        dragX.set(0);
      },
    });
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    isHorizontalDrag.current = null;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    const dx = e.touches[0].clientX - touchStartX.current;
    const dy = e.touches[0].clientY - touchStartY.current;

    if (isHorizontalDrag.current === null) {
      if (Math.abs(dx) > Math.abs(dy) + 5) isHorizontalDrag.current = true;
      else if (Math.abs(dy) > Math.abs(dx) + 5) isHorizontalDrag.current = false;
      return;
    }
    if (!isHorizontalDrag.current) return;

    let clamped = dx;
    if (dx > 0 && !prevRound) clamped = dx * 0.15;
    if (dx < 0 && !nextRound) clamped = dx * 0.15;
    dragX.set(clamped);
  };

  const handleTouchEnd = () => {
    if (!isHorizontalDrag.current) {
      touchStartX.current = null;
      touchStartY.current = null;
      isHorizontalDrag.current = null;
      return;
    }

    const current = dragX.get();
    const width = trackRef.current?.clientWidth ?? window.innerWidth;
    const threshold = width * 0.35;
    const capturedPrev = prevRound;
    const capturedNext = nextRound;

    if (current > threshold && capturedPrev) {
      slideTo(capturedPrev.id);
    } else if (current < -threshold && capturedNext) {
      slideTo(capturedNext.id);
    } else {
      animate(dragX, 0, { duration: 0.35, ease: [0.32, 0.72, 0, 1] });
    }

    touchStartX.current = null;
    touchStartY.current = null;
    isHorizontalDrag.current = null;
  };

  useEffect(() => {
    if (!data || selectedRoundId) return;
    const todays = pickTodaysRound(data.rounds);
    if (todays) setSelectedRoundId(todays.id);
  }, [data, selectedRoundId]);

  const fallback = data ? pickTodaysRound(data.rounds) : null;
  const round =
    (data && selectedRoundId
      ? (data.rounds.find(r => r.id === selectedRoundId) ?? fallback)
      : fallback) ?? null;

  usePrefetchLeaderboards(prevRound?.id ?? null, nextRound?.id ?? null, round?.month?.id ?? null);

  const revealSlotMutation = useRevealSlot();
  const selectCandidateMutation = useSelectCandidate();
  const enterRoundMutation = useEnterRound();
  const { mutate: enterRound } = enterRoundMutation;
  // One attempt per round per mount — a failed entry (round closed) must not
  // retry on every refetch, and success re-renders with a userRound anyway.
  const attemptedEntries = useRef<Set<string>>(new Set());

  // Auto-entry: looking at an enterable round you haven't joined registers you
  // and pre-draws the squad server-side; the refetch brings the hidden slots.
  // Rounds whose reveal windows have all passed are skipped — entering one
  // would draw a squad with nothing left to reveal.
  useEffect(() => {
    if (!round || round.userRound) return;
    if (round.status !== 'upcoming' && round.status !== 'revealing') return;
    const nowIso = new Date().toISOString();
    if (round.revealDays.length === 0 || round.revealDays.every(d => d.revealEnd <= nowIso)) return;
    if (attemptedEntries.current.has(round.id)) return;
    attemptedEntries.current.add(round.id);
    enterRound(round.id);
  }, [round, enterRound]);

  return (
    <motion.div
      className="fixed inset-0 flex flex-col bg-[#161616]"
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ duration: SLIDE_DURATION, ease: SLIDE_EASE }}
    >
      <main
        className="relative font-sans bg-[#161616] text-white flex flex-col flex-1 min-h-full touch-pan-y"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Stadium backdrop — anchored to the bottom so the pitch sits under the cards */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-cover bg-bottom bg-no-repeat scale-150 origin-bottom -translate-y-24"
          style={{ backgroundImage: 'url(/field.png)' }}
        />
        <TopNav
          rounds={data?.rounds ?? []}
          selectedRoundId={round?.id ?? null}
          onSelect={slideTo}
          now={now}
          dragX={dragX}
        />
        <div
          ref={trackRef}
          className="relative w-full max-w-[440px] mx-auto flex-1 overflow-hidden"
        >
          {isLoading && (
            <div className="absolute inset-0 px-4 pt-6 text-sm text-[#9AA9B7]">
              Loading your squad…
            </div>
          )}
          {isError && (
            <div className="absolute inset-0 px-4 pt-6 text-sm text-red-400">
              Could not load lobby data. Pull to retry.
            </div>
          )}
          {/*
            One keyed array rather than three fixed sibling positions. React
            matches array children by key across indices, so the round swiped to
            is moved rather than rebuilt. As separate siblings every panel's key
            changed on each swipe, remounting all three — cards and images
            included — on the frame the slide landed.
          */}
          {[
            { round: prevRound, offset: -1 },
            { round, offset: 0 },
            { round: nextRound, offset: 1 },
          ]
            .filter((p): p is { round: LobbyRound; offset: number } => p.round !== null)
            .map(({ round: panelRound, offset }) => (
              <motion.div
                key={panelRound.id}
                className="absolute top-0 bottom-0 w-full overflow-y-auto"
                style={{ x: dragX, left: `${offset * 100}%` }}
              >
                <RoundContent
                  round={panelRound}
                  matches={matches}
                  period={period}
                  cardsPerReveal={data?.contest.cardsPerReveal ?? 3}
                  onRevealSlot={slotId => revealSlotMutation.mutateAsync(slotId)}
                  onSelectCandidate={(slotId, candidateId) =>
                    selectCandidateMutation.mutateAsync({ slotId, candidateId })
                  }
                  entryError={
                    enterRoundMutation.isError && enterRoundMutation.variables === panelRound.id
                      ? i18n.t('lobby.enterError')
                      : undefined
                  }
                />
              </motion.div>
            ))}
        </div>
      </main>
      <BottomSheet
        roundId={round?.id ?? null}
        prizes={round?.prizes ?? []}
        month={round?.month ?? null}
        period={period}
        onPeriodChange={setPeriod}
        points={{
          goal: data?.contest.goalPoints ?? 0,
          shot: data?.contest.shotPoints ?? 0,
          tackle: data?.contest.tacklePoints ?? 0,
          interception: data?.contest.interceptionPoints ?? 0,
        }}
      />
    </motion.div>
  );
}
